import { API_BASE_URL } from 'config'

/** Redux and Kea */
import { kea } from 'kea'
import { put, call } from 'redux-saga/effects'
import { Validations } from 'validations'
import {
  csObjectToUnderscore,
  csObjectFromUnderscore,
  captureSentryException,
  getDatesYear
} from 'utils'

import PropTypes from 'prop-types'
import axios from 'axios'
import DataLogic from 'containers/App/logic'

import pick from 'lodash/pick'
import isEmpty from 'lodash/isEmpty'
import sortBy from 'lodash/sortBy'
import first from 'lodash/first'
import isArray from 'lodash/isArray'

/* eslint-disable camelcase */
const EQUIPMENT_ENDPOINT = `${API_BASE_URL}/public/simulator/equipment/`

/**
 * Map simple object to select component
 * @param {*} param0
 */
const selectMapper = ({ id, name, ...item }) => ({
  label: name,
  value: id,
  ...item,
  id,
  name
})

/**
 * Map energy classes to select
 * @param {*} param0
 */
export const selectMapperClasse = ({ classe_energetica, id, ...item }) => ({
  label: `${classe_energetica.name} ${getDatesYear(item.start_date, item.end_date)}`,
  value: id,
  ...item,
  classe_energetica,
  id
})

// Select the default class or the first if default doesnt exist
const selectDefault = (equipmentconsumption_set) => {
  const defaultClass = equipmentconsumption_set.find((item) => item.default === true)
  return defaultClass || first(equipmentconsumption_set)
}

/**
 * Class mapper
 * @param {*} param
 */
export const selectFirstClass = ({ equipmentconsumption_set, ...item }) => ({
  ...item,
  classes: equipmentconsumption_set && equipmentconsumption_set.map(selectMapperClasse),
  // Only one class? Select it if not use default
  selectedClass: selectDefault(equipmentconsumption_set)
})

const fields = {
  'hasSolarSystem': [
    Validations.isUndefined
  ],
  'hasSolarSystemYear': [
    Validations.hasSolarSystem,
    Validations.isUndefined
  ],
  'hasCentralAirConditioning': [
    Validations.isUndefined
  ],
  'centralHeatingOfferType': [
    Validations.hasCentralAirConditioning,
    Validations.isUndefined
  ],
  'centralAirConditioningEquipment': [
    Validations.hasCentralAirConditioning,
    Validations.isUndefined
  ],
  'centralAirConditioningEnergyClass': [
    Validations.hasCentralAirConditioning,
    Validations.isUndefined
  ]
}

export default kea({

  path: () => ['scenes', 'habitation'],

  actions: () => ({
    fetchData: (limit) => ({ limit }),
    setData: (data) => ({ data }),
    setInvalid: (invalid) => ({ invalid }),

    setDataFetchError: (fetchDataError) => ({ fetchDataError })
  }),

  reducers: ({ actions }) => ({
    data: [{}, PropTypes.object, {
      [actions.setData]: (state, payload) => ({ ...payload.data })
    }],

    loading: [true, PropTypes.bool, {
      [actions.fetchData]: () => true,
      [actions.setData]: () => false,
      [actions.setDataFetchError]: () => false
    }],

    fields: [fields, PropTypes.object, {}],

    invalid: [[], PropTypes.array, {
      [actions.setInvalid]: (_, payload) => ([ ...payload.invalid ])
    }],

    error: [false, PropTypes.array, {
      [actions.fetchData]: () => false,
      [actions.setData]: () => false,
      [actions.setDataFetchError]: (_, payload) => payload.fetchDataError
    }],

    dataChanged: [false, PropTypes.bool, {
      [actions.setValues]: (_, payload) => {
        if (payload.municipality ||
            payload.offerType ||
            payload.adults ||
            payload.children ||
            payload.elderly ||
            payload.constructionYear ||
            payload.equipmentUtilizationProfile
        ) {
          return true
        } else {
          return false
        }
      }
    }]
  }),

  takeLatest: ({ actions, workers }) => ({
    [actions.fetchData]: workers.fetchData,
    [actions.setValues]: workers.shouldRefreshData
  }),

  start: function * () {
    const { fetchData } = this.actions
    const data = yield this.get('data')
    const error = yield this.get('error')
    const dataChanged = yield this.get('dataChanged')
    const { profileFinished } = yield DataLogic.get('finished')

    // https://sentry2.ubiwhere.com/ubiwhere/simulador-avancado/issues/3364/
    // Be very carefull when changing this start function because
    // We import this logic and the simple habitation logic in the profile logic
    // This creates a infinite loop of start everytime we change go to the profile the equipment start is engaged
    // This is a problem with kea itself
    // But we needed because of this issue https://gitlab.ubiwhere.com/adene-poupa-energia/adene-simulador-avancado/issues/69
    // For the shouldRefreshData worker to keep working when unmounted
    // Changing this code can make side effects so please be aware
    // When props don't exists it means it comes from an import so we dont do anything
    if (!this.props) {
      return
    }

    if (!profileFinished) {
      this.props.router.push('/')
    } else {
      if (isEmpty(data) || error || dataChanged) {
        yield put(fetchData(1))
      }
    }
  },

  selectors: ({ selectors }) => ({
    divisions: [
      () => [selectors.data],
      ({ divisions }) => divisions,
      PropTypes.any
    ],

    firstDivision: [
      () => [selectors.data],
      ({ divisions }) => first(divisions),
      PropTypes.any
    ]
  }),

  workers: {
    * fetchData (action) {
      const {
        setData,
        setValues,
        setDataFetchError
      } = this.actions

      const values = yield DataLogic.get('values')
      const settings = yield DataLogic.get('settings')

      let params = csObjectToUnderscore(
        pick(values, [
          'constructionYear',
          'equipmentUtilizationType',
          'equipmentUtilizationProfile',
          'offerType'
        ])
      )

      const { adults, children, elderly } = values
      params.habitants = (adults + children + elderly) || 0

      try {
        const response = yield call(axios.get, EQUIPMENT_ENDPOINT, { params })
        const data = csObjectFromUnderscore(response.data)
        const {
            offerTypes,
            solarSystemYears,
            centralHeating,
            houseDivisions,
            equipment,
            suggestedEquipment,
            energyClasses
          } = data

        let cleanData = {}

        const quantitiesLogic = (item) => {
          let qtn = item.t1_factor_quantity

          if (params.habitants > 2) {
            let nr = params.habitants - 1
            nr = nr > 6 ? 6 : nr // haha
            qtn = item[`t${nr}_factor_quantity`]
          }

          return qtn || 1
        }

        const quantities = (item) => ({
          ...item,
          quantity: quantitiesLogic(item, params.habitants)
        })

        cleanData.equipments = equipment
          .map(selectFirstClass)
          .map(quantities)

        cleanData.divisions = sortBy(houseDivisions, 'order')

        cleanData.offerTypes = sortBy(
          offerTypes
            .map(selectMapper),
            'order'
        )

        cleanData.solarSystemYears = sortBy(
          solarSystemYears
            .map(selectMapper),
            'order'
        )

        cleanData.centralHeatingEquipments = sortBy(
          centralHeating
            .map(selectFirstClass)
            .map(quantities)
            .map(selectMapper),
            'order'
        )

        cleanData.sugestedEquipment = sortBy(
            suggestedEquipment
              .map(selectFirstClass)
              .map(quantities),
            'order'
        )

        // Duplicate equipments for each house division it belongs
        // https://gitlab.ubiwhere.com/adene-poupa-energia/adenepe-portal-publico/issues/129
        let duplicates = []
        cleanData.sugestedEquipment = cleanData.sugestedEquipment.map((equipment) => {
          if (isArray(equipment.house_divisions)) {
            equipment.house_divisions.map((division, index) => {
              if (index) { // Ignore the first
                // Create a clone so we dont change the current object
                // Set the new house division to the clone
                // Add element to array
                const equipmentClone = { ...equipment }
                equipmentClone.house_divisions = division
                duplicates.push(equipmentClone)
              }
              return division
            })

              // Set array to the first element
            equipment.house_divisions = equipment.house_divisions[0]
          }

          return equipment
        })

        // Merge duplicates and singles
        cleanData.sugestedEquipment = [...cleanData.sugestedEquipment, ...duplicates]

        // Duplicate central equipments for each house division it belongs
        // https://gitlab.ubiwhere.com/adene-poupa-energia/adenepe-portal-publico/issues/129
        duplicates = []
        cleanData.centralHeatingEquipments = cleanData.centralHeatingEquipments.map((equipment) => {
          if (isArray(equipment.house_divisions)) {
            equipment.house_divisions.map((division, index) => {
              if (index) { // Ignore the first
                // Create a clone so we dont change the current object
                // Set the new house division to the clone
                // Add element to array
                const equipmentClone = { ...equipment }
                equipmentClone.house_divisions = division
                duplicates.push(equipmentClone)
              }
              return division
            })

              // Set array to the first element
            equipment.house_divisions = equipment.house_divisions[0]
          }

          return equipment
        })

        // Merge duplicates and singles
        cleanData.centralHeatingEquipments = [...cleanData.centralHeatingEquipments, ...duplicates]

        cleanData.energyClasses =
          energyClasses
            .map(selectMapper)
            .sort((a, b) => {
              var nameA = a.label.toUpperCase()
              var nameB = b.label.toUpperCase()
              if (nameA < nameB) {
                return -1
              }
              if (nameA > nameB) {
                return 1
              }

              return 0
            })
            .sort((a, b) => {
              let plusA = a.label.substring(1, a.label.length)
              let plusB = b.label.substring(1, b.label.length)

              if (plusA > plusB) {
                return -1
              }

              if (plusA < plusB) {
                return 1
              }

              return 0
            })

        // TODO: Some fields here shouldn't be updated WHEN changing data after simulation, fix ASAP
        const defaultValues = {
          equipments: cleanData.sugestedEquipment,
          hasSolarSystem: false,
          hasCentralAirConditioning: false,
          centralHeatingOfferType: values.offerType,
          solarSystemYear: 1
        }

        yield put(setData({ ...cleanData }))

        if (!settings.isFromProfile) {
          yield put(setValues(defaultValues))
        }
      } catch (error) {
        // 404 ? Redirect user
        if (error.response.status === 404) {
          if (this.props) {
            this.props.router.push('/')
          }
        } else {
          captureSentryException(error)
          yield put(setDataFetchError(error))
        }
      }
    },

    /**
     * If offer type changes force the equipments to reload
     * Should solve the issue in
     * https://gitlab.ubiwhere.com/adene-poupa-energia/adene-simulador-avancado/issues/69
     * @param {*} action
     */
    * shouldRefreshData (action) {
      const { fetchData } = this.actions
      const { profileFinished } = yield DataLogic.get('finished')
      const payload = action.payload
      if (profileFinished && payload.offerType) {
        yield put(fetchData(1))
      }
    }
  },

  connect: {
    actions: [
      DataLogic, ['setValue', 'setValues', 'setFinished']
    ],
    props: [
      DataLogic, ['values', 'translations']
    ]
  }
})



// WEBPACK FOOTER //
// ./src/containers/Habitation/logic.js